import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '../../components/ui/Card';
import { Alert } from '../../components/ui/Alert';
import { Table } from '../../components/ui/Table';
import { referralAdminService } from '../../services/admin';
import type {
  AdminReferralOverview,
  AdminTopReferrer,
  AdminReferralChannel,
  AdminReferralGeo,
  AdminReferralReward,
  AdminReferralFraud,
  AdminReferralBlacklistEntry,
  AdminAffiliatePayout,
  AdminReferralProgram,
  AdminTrackingCode,
} from '../../types/admin';

export const ReferralAdminDashboardPage: React.FC = () => {
  const { t } = useTranslation('admin');
  const [overview, setOverview] = useState<AdminReferralOverview | null>(null);
  const [leaderboard, setLeaderboard] = useState<AdminTopReferrer[]>([]);
  const [channels, setChannels] = useState<AdminReferralChannel[]>([]);
  const [geo, setGeo] = useState<AdminReferralGeo[]>([]);
  const [rewards, setRewards] = useState<AdminReferralReward[]>([]);
  const [fraudQueue, setFraudQueue] = useState<AdminReferralFraud[]>([]);
  const [blacklist, setBlacklist] = useState<AdminReferralBlacklistEntry[]>([]);
  const [payouts, setPayouts] = useState<AdminAffiliatePayout[]>([]);
  const [programs, setPrograms] = useState<AdminReferralProgram[]>([]);
  const [selectedProgramId, setSelectedProgramId] = useState<number | null>(null);
  const [programForm, setProgramForm] = useState({
    is_enabled: true,
    allow_custom_codes: true,
    public_leaderboard: false,
    code_expiration_days: '',
    reward_expiration_days: '',
    default_reward_credit_amount: '',
    default_referee_discount_amount: '',
    reward_currency: 'usd',
    tiered_rewards: '',
    bonus_milestones: '',
    fraud_settings: '',
    affiliate_settings: '',
    sharing_messages: '',
  });
  const [rewardStatus, setRewardStatus] = useState('all');
  const [payoutStatus, setPayoutStatus] = useState('all');
  const [blacklistType, setBlacklistType] = useState<'email' | 'ip' | 'user_id'>('email');
  const [blacklistValue, setBlacklistValue] = useState('');
  const [blacklistReason, setBlacklistReason] = useState('');
  const [trackingCodes, setTrackingCodes] = useState<AdminTrackingCode[]>([]);
  const [trackingForm, setTrackingForm] = useState({ label: '', code: '', max_uses: '', expires_at: '' });
  const [trackingFormOpen, setTrackingFormOpen] = useState(false);
  const [trackingSubmitting, setTrackingSubmitting] = useState(false);
  const [copiedId, setCopiedId] = useState<number | null>(null);
  const [trackingLang, setTrackingLang] = useState<string>('en');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const buildTrackingUrl = (shareUrl: string, lang: string): string => {
    try {
      const url = new URL(shareUrl);
      // Insert language prefix before the path: /register -> /en/register
      url.pathname = '/' + lang + url.pathname;
      return url.toString();
    } catch {
      return shareUrl;
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [overviewData, topReferrers, channelData, geoData, rewardData, fraudData, blacklistData, payoutData, programData, trackingData] = await Promise.all([
        referralAdminService.getOverview(),
        referralAdminService.getTopReferrers({ period: 'monthly' }),
        referralAdminService.getChannels(),
        referralAdminService.getGeo(),
        referralAdminService.getRewards(rewardStatus === 'all' ? {} : { status: rewardStatus }),
        referralAdminService.getFraud(),
        referralAdminService.getBlacklist(),
        referralAdminService.getPayouts(payoutStatus === 'all' ? {} : { status: payoutStatus }),
        referralAdminService.getPrograms(),
        referralAdminService.getTrackingCodes(),
      ]);

      setOverview(overviewData);
      setLeaderboard(topReferrers.leaderboard);
      setChannels(channelData.channels);
      setGeo(geoData.geo);
      setRewards(rewardData.rewards.data ?? []);
      setFraudQueue(fraudData.data ?? []);
      setBlacklist(blacklistData.data ?? []);
      setPayouts(payoutData.data ?? []);
      setPrograms(programData.data ?? []);
      setTrackingCodes(trackingData.codes ?? []);
      if (programData.data?.length && !selectedProgramId) {
        setSelectedProgramId(programData.data[0].id);
        setProgramForm(serializeProgram(programData.data[0]));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('referrals_admin.loading_error'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [rewardStatus, payoutStatus]);

  useEffect(() => {
    if (!selectedProgramId || programs.length === 0) {
      return;
    }
    const program = programs.find((item) => item.id === selectedProgramId);
    if (program) {
      setProgramForm(serializeProgram(program));
    }
  }, [selectedProgramId, programs]);

  const rewardColumns = useMemo(() => ([
    { key: 'id', header: t('referrals_admin.rewards.table.id') },
    { key: 'type', header: t('referrals_admin.rewards.table.type') },
    { key: 'amount', header: t('referrals_admin.rewards.table.amount'), render: (val: AdminReferralReward['amount']) => `$${Number(val).toFixed(2)}` },
    { key: 'status', header: t('referrals_admin.rewards.table.status') },
    { key: 'created_at', header: t('referrals_admin.rewards.table.created') },
  ]), [t]);

  const fraudColumns = useMemo(() => ([
    { key: 'id', header: t('referrals_admin.fraud.table.id') },
    { key: 'fraud_reason', header: t('referrals_admin.fraud.table.reason') },
    { key: 'review_status', header: t('referrals_admin.fraud.table.status') },
    { key: 'created_at', header: t('referrals_admin.fraud.table.created') },
  ]), [t]);

  const blacklistColumns = useMemo(() => ([
    { key: 'id', header: t('referrals_admin.blacklist.table.id') },
    { key: 'type', header: t('referrals_admin.blacklist.table.type') },
    { key: 'value', header: t('referrals_admin.blacklist.table.value') },
    { key: 'reason', header: t('referrals_admin.blacklist.table.reason') },
    { key: 'created_at', header: t('referrals_admin.blacklist.table.created') },
  ]), [t]);

  const payoutColumns = useMemo(() => ([
    { key: 'id', header: t('referrals_admin.payouts.table.id') },
    { key: 'amount', header: t('referrals_admin.payouts.table.amount'), render: (val: AdminAffiliatePayout['amount']) => `$${Number(val).toFixed(2)}` },
    { key: 'status', header: t('referrals_admin.payouts.table.status') },
    { key: 'method', header: t('referrals_admin.payouts.table.method'), render: (val: AdminAffiliatePayout['method']) => val ?? t('referrals_admin.payouts.table.method_unknown') },
    { key: 'created_at', header: t('referrals_admin.payouts.table.created') },
  ]), [t]);

  const handleFraudReview = async (referralId: number, reviewStatus: 'approved' | 'rejected' | 'blocked') => {
    try {
      await referralAdminService.reviewFraud(referralId, { review_status: reviewStatus });
      fetchData();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('referrals_admin.fraud.review_error'));
    }
  };

  const handleBlacklistAdd = async () => {
    if (!blacklistValue.trim()) {
      setError(t('referrals_admin.blacklist.value_required'));
      return;
    }

    try {
      await referralAdminService.addBlacklist({
        type: blacklistType,
        value: blacklistValue.trim(),
        reason: blacklistReason.trim() || undefined,
      });
      setBlacklistValue('');
      setBlacklistReason('');
      fetchData();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('referrals_admin.blacklist.add_error'));
    }
  };

  const handleBlacklistDelete = async (entryId: number) => {
    try {
      await referralAdminService.deleteBlacklist(entryId);
      fetchData();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('referrals_admin.blacklist.delete_error'));
    }
  };

  const handlePayoutPaid = async (payoutId: number) => {
    try {
      await referralAdminService.markPayoutPaid(payoutId);
      fetchData();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('referrals_admin.payouts.update_error'));
    }
  };

  const serializeProgram = (program: AdminReferralProgram) => ({
    is_enabled: program.is_enabled,
    allow_custom_codes: program.allow_custom_codes,
    public_leaderboard: program.public_leaderboard,
    code_expiration_days: program.code_expiration_days?.toString() ?? '',
    reward_expiration_days: program.reward_expiration_days?.toString() ?? '',
    default_reward_credit_amount: program.default_reward_credit_amount ?? '',
    default_referee_discount_amount: program.default_referee_discount_amount ?? '',
    reward_currency: program.reward_currency ?? 'usd',
    tiered_rewards: program.tiered_rewards ? JSON.stringify(program.tiered_rewards, null, 2) : '',
    bonus_milestones: program.bonus_milestones ? JSON.stringify(program.bonus_milestones, null, 2) : '',
    fraud_settings: program.fraud_settings ? JSON.stringify(program.fraud_settings, null, 2) : '',
    affiliate_settings: program.affiliate_settings ? JSON.stringify(program.affiliate_settings, null, 2) : '',
    sharing_messages: program.sharing_messages ? JSON.stringify(program.sharing_messages, null, 2) : '',
  });

  const handleProgramSave = async () => {
    if (!selectedProgramId) {
      setError(t('referrals_admin.programs.select_required'));
      return;
    }

    const parseJson = (value: string, label: string) => {
      if (!value.trim()) return null;
      try {
        return JSON.parse(value);
      } catch {
        throw new Error(t('referrals_admin.programs.json_error', { label }));
      }
    };

    try {
      const payload = {
        is_enabled: programForm.is_enabled,
        allow_custom_codes: programForm.allow_custom_codes,
        public_leaderboard: programForm.public_leaderboard,
        code_expiration_days: programForm.code_expiration_days ? Number(programForm.code_expiration_days) : null,
        reward_expiration_days: programForm.reward_expiration_days ? Number(programForm.reward_expiration_days) : null,
        default_reward_credit_amount: Number(programForm.default_reward_credit_amount || 0),
        default_referee_discount_amount: Number(programForm.default_referee_discount_amount || 0),
        reward_currency: programForm.reward_currency,
        tiered_rewards: parseJson(programForm.tiered_rewards, t('referrals_admin.programs.fields.tiered_rewards')),
        bonus_milestones: parseJson(programForm.bonus_milestones, t('referrals_admin.programs.fields.bonus_milestones')),
        fraud_settings: parseJson(programForm.fraud_settings, t('referrals_admin.programs.fields.fraud_settings')),
        affiliate_settings: parseJson(programForm.affiliate_settings, t('referrals_admin.programs.fields.affiliate_settings')),
        sharing_messages: parseJson(programForm.sharing_messages, t('referrals_admin.programs.fields.sharing_messages')),
      };

      await referralAdminService.updateProgram(selectedProgramId, payload);
      fetchData();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('referrals_admin.programs.save_error'));
    }
  };

  const handleExportPayouts = () => {
    if (payouts.length === 0) {
      setError(t('referrals_admin.payouts.export_empty'));
      return;
    }

    const rows = [
      ['id', 'user_email', 'amount', 'currency', 'status', 'method', 'created_at'],
      ...payouts.map((payout) => [
        payout.id,
        payout.user?.email ?? '',
        payout.amount,
        payout.currency,
        payout.status,
        payout.method ?? '',
        payout.created_at,
      ]),
    ];

    const csv = rows.map((row) => row.map((value) => `"${String(value).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'referral_payouts.csv';
    link.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (!overview) {
    return null;
  }

  const { kpis, success_metrics: successMetrics, funnel } = overview.overview;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl md:text-2xl font-bold text-gray-900">{t('referrals_admin.page_title')}</h1>
        <p className="mt-1 text-sm text-gray-500">{t('referrals_admin.page_subtitle')}</p>
      </div>

      {error && (
        <Alert variant="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
        <Card>
          <h3 className="text-sm font-medium text-gray-500">{t('referrals_admin.kpis.clicks')}</h3>
          <p className="mt-2 text-3xl font-bold text-gray-900">{kpis.clicks}</p>
        </Card>
        <Card>
          <h3 className="text-sm font-medium text-gray-500">{t('referrals_admin.kpis.signups')}</h3>
          <p className="mt-2 text-3xl font-bold text-gray-900">{kpis.signups}</p>
          <p className="mt-2 text-xs text-gray-500">
            {t('referrals_admin.kpis.signup_rate', { value: kpis.signup_rate })}
          </p>
        </Card>
        <Card>
          <h3 className="text-sm font-medium text-gray-500">{t('referrals_admin.kpis.conversions')}</h3>
          <p className="mt-2 text-3xl font-bold text-gray-900">{kpis.conversions}</p>
          <p className="mt-2 text-xs text-gray-500">
            {t('referrals_admin.kpis.conversion_rate', { value: kpis.conversion_rate })}
          </p>
        </Card>
        <Card>
          <h3 className="text-sm font-medium text-gray-500">{t('referrals_admin.kpis.revenue')}</h3>
          <p className="mt-2 text-3xl font-bold text-gray-900">${kpis.revenue.toFixed(2)}</p>
          <p className="mt-2 text-xs text-gray-500">
            {t('referrals_admin.kpis.roi', { value: kpis.roi })}
          </p>
        </Card>
        <Card>
          <h3 className="text-sm font-medium text-gray-500">{t('referrals_admin.kpis.rewards')}</h3>
          <p className="mt-2 text-3xl font-bold text-gray-900">${kpis.rewards_total.toFixed(2)}</p>
          <p className="mt-2 text-xs text-gray-500">
            {t('referrals_admin.kpis.pending_rewards', { value: kpis.pending_rewards })}
          </p>
        </Card>
        <Card>
          <h3 className="text-sm font-medium text-gray-500">{t('referrals_admin.kpis.avg_ltv')}</h3>
          <p className="mt-2 text-3xl font-bold text-gray-900">${kpis.average_ltv.toFixed(2)}</p>
        </Card>
        <Card>
          <h3 className="text-sm font-medium text-gray-500">{t('referrals_admin.kpis.cpa')}</h3>
          <p className="mt-2 text-3xl font-bold text-gray-900">${kpis.cost_per_acquisition.toFixed(2)}</p>
        </Card>
        <Card>
          <h3 className="text-sm font-medium text-gray-500">{t('referrals_admin.kpis.verified')}</h3>
          <p className="mt-2 text-3xl font-bold text-gray-900">{kpis.verified}</p>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card>
          <h3 className="text-lg font-semibold text-gray-900">{t('referrals_admin.funnel.title')}</h3>
          <div className="mt-4 space-y-3 text-sm text-gray-600">
            <div className="flex items-center justify-between">
              <span>{t('referrals_admin.funnel.clicks')}</span>
              <span className="font-semibold text-gray-900">{funnel.clicks}</span>
            </div>
            <div className="flex items-center justify-between">
              <span>{t('referrals_admin.funnel.signups')}</span>
              <span className="font-semibold text-gray-900">{funnel.signups}</span>
            </div>
            <div className="flex items-center justify-between">
              <span>{t('referrals_admin.funnel.verified')}</span>
              <span className="font-semibold text-gray-900">{funnel.verified}</span>
            </div>
            <div className="flex items-center justify-between">
              <span>{t('referrals_admin.funnel.conversions')}</span>
              <span className="font-semibold text-gray-900">{funnel.conversions}</span>
            </div>
          </div>
        </Card>

        <Card>
          <h3 className="text-lg font-semibold text-gray-900">{t('referrals_admin.success.title')}</h3>
          <div className="mt-4 space-y-3 text-sm text-gray-600">
            <div className="flex items-center justify-between">
              <span>{t('referrals_admin.success.signup_rate')}</span>
              <span className="font-semibold text-gray-900">{successMetrics.referral_signup_rate}%</span>
            </div>
            <div className="flex items-center justify-between">
              <span>{t('referrals_admin.success.conversion_rate')}</span>
              <span className="font-semibold text-gray-900">{successMetrics.referral_to_paid_conversion_rate}%</span>
            </div>
            <div className="flex items-center justify-between">
              <span>{t('referrals_admin.success.viral')}</span>
              <span className="font-semibold text-gray-900">{successMetrics.viral_coefficient}</span>
            </div>
            <div className="flex items-center justify-between">
              <span>{t('referrals_admin.success.cac_reduction')}</span>
              <span className="font-semibold text-gray-900">{successMetrics.cac_reduction}%</span>
            </div>
          </div>
        </Card>

        <Card>
          <h3 className="text-lg font-semibold text-gray-900">{t('referrals_admin.top_referrers.title')}</h3>
          <div className="mt-4 space-y-2 text-sm text-gray-600">
            {leaderboard.length === 0 && (
              <p className="text-sm text-gray-500">{t('referrals_admin.top_referrers.empty')}</p>
            )}
            {leaderboard.slice(0, 5).map((entry) => (
              <div key={entry.user_id} className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-900">#{entry.rank} {entry.name}</p>
                  <p className="text-xs text-gray-500">{entry.email}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-900">{entry.conversions}</p>
                  <p className="text-xs text-gray-500">{t('referrals_admin.top_referrers.conversions')}</p>
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <h3 className="text-lg font-semibold text-gray-900">{t('referrals_admin.channels.title')}</h3>
          <div className="mt-4 space-y-2 text-sm text-gray-600">
            {channels.slice(0, 6).map((channel, index) => (
              <div key={`${channel.source}-${channel.medium}-${index}`} className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-900">{channel.source ?? t('referrals_admin.channels.unknown')}</p>
                  <p className="text-xs text-gray-500">
                    {(channel.medium ?? t('referrals_admin.channels.none'))} • {(channel.campaign ?? t('referrals_admin.channels.none'))}
                  </p>
                </div>
                <span className="text-sm font-semibold text-gray-900">{channel.total}</span>
              </div>
            ))}
          </div>
        </Card>

        <Card>
          <h3 className="text-lg font-semibold text-gray-900">{t('referrals_admin.geo.title')}</h3>
          <div className="mt-4 space-y-2 text-sm text-gray-600">
            {geo.slice(0, 6).map((item) => (
              <div key={item.country} className="flex items-center justify-between">
                <span>{item.country}</span>
                <span className="text-sm font-semibold text-gray-900">{item.total}</span>
              </div>
            ))}
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">{t('referrals_admin.rewards.title')}</h3>
            <select
              value={rewardStatus}
              onChange={(event) => setRewardStatus(event.target.value)}
              className="text-sm rounded-md border-gray-200"
            >
              <option value="all">{t('referrals_admin.rewards.filters.all')}</option>
              <option value="pending">{t('referrals_admin.rewards.filters.pending')}</option>
              <option value="applied">{t('referrals_admin.rewards.filters.applied')}</option>
              <option value="expired">{t('referrals_admin.rewards.filters.expired')}</option>
            </select>
          </div>
          <div className="mt-4">
            <Table
              columns={rewardColumns}
              data={rewards}
              emptyMessage={t('referrals_admin.rewards.empty')}
            />
          </div>
        </Card>

        <Card>
          <h3 className="text-lg font-semibold text-gray-900">{t('referrals_admin.fraud.title')}</h3>
          <div className="mt-4">
            <Table
              columns={fraudColumns}
              data={fraudQueue}
              emptyMessage={t('referrals_admin.fraud.empty')}
              renderActions={(row) => (
                <div className="flex gap-2">
                  <button
                    className="text-xs text-green-600"
                    onClick={(event) => {
                      event.stopPropagation();
                      handleFraudReview(row.id, 'approved');
                    }}
                  >
                    {t('referrals_admin.fraud.actions.approve')}
                  </button>
                  <button
                    className="text-xs text-red-600"
                    onClick={(event) => {
                      event.stopPropagation();
                      handleFraudReview(row.id, 'rejected');
                    }}
                  >
                    {t('referrals_admin.fraud.actions.reject')}
                  </button>
                  <button
                    className="text-xs text-gray-600"
                    onClick={(event) => {
                      event.stopPropagation();
                      handleFraudReview(row.id, 'blocked');
                    }}
                  >
                    {t('referrals_admin.fraud.actions.block')}
                  </button>
                </div>
              )}
            />
          </div>
        </Card>
      </div>

      <Card>
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{t('referrals_admin.blacklist.title')}</h3>
            <p className="text-sm text-gray-500">{t('referrals_admin.blacklist.subtitle')}</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <select
              value={blacklistType}
              onChange={(event) => setBlacklistType(event.target.value as 'email' | 'ip' | 'user_id')}
              className="text-sm rounded-md border-gray-200"
            >
              <option value="email">{t('referrals_admin.blacklist.types.email')}</option>
              <option value="ip">{t('referrals_admin.blacklist.types.ip')}</option>
              <option value="user_id">{t('referrals_admin.blacklist.types.user_id')}</option>
            </select>
            <input
              value={blacklistValue}
              onChange={(event) => setBlacklistValue(event.target.value)}
              placeholder={t('referrals_admin.blacklist.value_placeholder')}
              className="text-sm rounded-md border-gray-200 px-3 py-2"
            />
            <input
              value={blacklistReason}
              onChange={(event) => setBlacklistReason(event.target.value)}
              placeholder={t('referrals_admin.blacklist.reason_placeholder')}
              className="text-sm rounded-md border-gray-200 px-3 py-2"
            />
            <button
              className="text-sm px-3 py-2 bg-gray-900 text-white rounded-md"
              onClick={handleBlacklistAdd}
            >
              {t('referrals_admin.blacklist.add_button')}
            </button>
          </div>
        </div>
        <div className="mt-4">
          <Table
            columns={blacklistColumns}
            data={blacklist}
            emptyMessage={t('referrals_admin.blacklist.empty')}
            renderActions={(row) => (
              <button
                className="text-xs text-red-600"
                onClick={(event) => {
                  event.stopPropagation();
                  handleBlacklistDelete(row.id);
                }}
              >
                {t('referrals_admin.blacklist.actions.remove')}
              </button>
            )}
          />
        </div>
      </Card>

      <Card>
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{t('referrals_admin.payouts.title')}</h3>
            <p className="text-sm text-gray-500">{t('referrals_admin.payouts.subtitle')}</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <select
              value={payoutStatus}
              onChange={(event) => setPayoutStatus(event.target.value)}
              className="text-sm rounded-md border-gray-200"
            >
              <option value="all">{t('referrals_admin.payouts.filters.all')}</option>
              <option value="pending">{t('referrals_admin.payouts.filters.pending')}</option>
              <option value="paid">{t('referrals_admin.payouts.filters.paid')}</option>
            </select>
            <button
              className="text-sm px-3 py-2 border border-gray-200 rounded-md"
              onClick={handleExportPayouts}
            >
              {t('referrals_admin.payouts.export_button')}
            </button>
          </div>
        </div>
        <div className="mt-4">
          <Table
            columns={payoutColumns}
            data={payouts}
            emptyMessage={t('referrals_admin.payouts.empty')}
            renderActions={(row) => (
              <button
                className="text-xs text-indigo-600"
                disabled={row.status === 'paid'}
                onClick={(event) => {
                  event.stopPropagation();
                  if (row.status !== 'paid') {
                    handlePayoutPaid(row.id);
                  }
                }}
              >
                {row.status === 'paid'
                  ? t('referrals_admin.payouts.actions.paid')
                  : t('referrals_admin.payouts.actions.mark_paid')}
              </button>
            )}
          />
        </div>
      </Card>

      <Card>
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <div>
            <h3 className="text-xl font-bold text-gray-900">{t('referrals_admin.programs.title')}</h3>
            <p className="mt-1 text-sm text-gray-500">{t('referrals_admin.programs.subtitle')}</p>
          </div>
          <div className="w-full md:w-auto">
            <label className="block text-sm font-medium text-gray-700 mb-1">Select Account</label>
            <select
              value={selectedProgramId ?? ''}
              onChange={(event) => setSelectedProgramId(Number(event.target.value))}
              className="w-full md:w-64 rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {programs.map((program) => (
                <option key={program.id} value={program.id}>
                  {program.account?.name ?? `Account ${program.account_id}`}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left Column - Basic Settings */}
          <div className="space-y-4">
            <h4 className="text-sm font-semibold text-gray-900 border-b border-gray-200 pb-2">Basic Settings</h4>

            <div className="space-y-3">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={programForm.is_enabled}
                  onChange={(event) => setProgramForm({ ...programForm, is_enabled: event.target.checked })}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700">{t('referrals_admin.programs.fields.enabled')}</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={programForm.allow_custom_codes}
                  onChange={(event) => setProgramForm({ ...programForm, allow_custom_codes: event.target.checked })}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700">{t('referrals_admin.programs.fields.custom_codes')}</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={programForm.public_leaderboard}
                  onChange={(event) => setProgramForm({ ...programForm, public_leaderboard: event.target.checked })}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700">{t('referrals_admin.programs.fields.public_leaderboard')}</span>
              </label>
            </div>

            <h4 className="text-sm font-semibold text-gray-900 border-b border-gray-200 pb-2 mt-6">Reward Settings</h4>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('referrals_admin.programs.fields.code_expiration_days')}
              </label>
              <input
                type="number"
                value={programForm.code_expiration_days}
                onChange={(event) => setProgramForm({ ...programForm, code_expiration_days: event.target.value })}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Leave empty for no expiration"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('referrals_admin.programs.fields.reward_expiration_days')}
              </label>
              <input
                type="number"
                value={programForm.reward_expiration_days}
                onChange={(event) => setProgramForm({ ...programForm, reward_expiration_days: event.target.value })}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Leave empty for no expiration"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('referrals_admin.programs.fields.default_reward_credit_amount')}
              </label>
              <input
                type="number"
                value={programForm.default_reward_credit_amount}
                onChange={(event) => setProgramForm({ ...programForm, default_reward_credit_amount: event.target.value })}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                step="0.01"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('referrals_admin.programs.fields.default_referee_discount_amount')}
              </label>
              <input
                type="number"
                value={programForm.default_referee_discount_amount}
                onChange={(event) => setProgramForm({ ...programForm, default_referee_discount_amount: event.target.value })}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                step="0.01"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('referrals_admin.programs.fields.reward_currency')}
              </label>
              <input
                type="text"
                value={programForm.reward_currency}
                onChange={(event) => setProgramForm({ ...programForm, reward_currency: event.target.value })}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 uppercase"
                placeholder="USD"
                maxLength={3}
              />
            </div>
          </div>

          {/* Right Column - Advanced Settings (JSON) */}
          <div className="space-y-4">
            <h4 className="text-sm font-semibold text-gray-900 border-b border-gray-200 pb-2">Advanced Settings (JSON)</h4>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('referrals_admin.programs.fields.tiered_rewards')}
              </label>
              <textarea
                value={programForm.tiered_rewards}
                onChange={(event) => setProgramForm({ ...programForm, tiered_rewards: event.target.value })}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows={4}
                placeholder='{"min": 5, "max": 10, "amount": 50}'
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('referrals_admin.programs.fields.bonus_milestones')}
              </label>
              <textarea
                value={programForm.bonus_milestones}
                onChange={(event) => setProgramForm({ ...programForm, bonus_milestones: event.target.value })}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows={4}
                placeholder='{"count": 10, "amount": 100}'
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('referrals_admin.programs.fields.fraud_settings')}
              </label>
              <textarea
                value={programForm.fraud_settings}
                onChange={(event) => setProgramForm({ ...programForm, fraud_settings: event.target.value })}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows={4}
                placeholder='{"max_same_ip": 3, "max_same_device": 5}'
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('referrals_admin.programs.fields.affiliate_settings')}
              </label>
              <textarea
                value={programForm.affiliate_settings}
                onChange={(event) => setProgramForm({ ...programForm, affiliate_settings: event.target.value })}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows={4}
                placeholder='{"commission_rate": 0.1, "min_payout": 50}'
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('referrals_admin.programs.fields.sharing_messages')}
              </label>
              <textarea
                value={programForm.sharing_messages}
                onChange={(event) => setProgramForm({ ...programForm, sharing_messages: event.target.value })}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows={4}
                placeholder='{"email": "Join me...", "social": "Check this out..."}'
              />
            </div>
          </div>
        </div>

        <div className="mt-6 flex justify-end border-t border-gray-200 pt-4">
          <button
            className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-md text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
            onClick={handleProgramSave}
          >
            {t('referrals_admin.programs.save_button')}
          </button>
        </div>
      </Card>

      {/* Tracking Links */}
      <Card>
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-xl font-bold text-gray-900">{t('referrals_admin.tracking.title')}</h3>
            <p className="mt-1 text-sm text-gray-500">{t('referrals_admin.tracking.subtitle')}</p>
          </div>
          <button
            className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-md text-sm font-medium transition-colors"
            onClick={() => setTrackingFormOpen((v) => !v)}
          >
            {trackingFormOpen ? t('referrals_admin.tracking.cancel') : t('referrals_admin.tracking.new_button')}
          </button>
        </div>

        {trackingFormOpen && (
          <div className="mb-6 bg-gray-50 rounded-lg p-4 border border-gray-200">
            <h4 className="text-sm font-semibold text-gray-900 mb-4">{t('referrals_admin.tracking.form_title')}</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">{t('referrals_admin.tracking.label_field')} *</label>
                <input
                  type="text"
                  value={trackingForm.label}
                  onChange={(e) => setTrackingForm({ ...trackingForm, label: e.target.value })}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder={t('referrals_admin.tracking.label_placeholder')}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">{t('referrals_admin.tracking.code_field')}</label>
                <input
                  type="text"
                  value={trackingForm.code}
                  onChange={(e) => setTrackingForm({ ...trackingForm, code: e.target.value.toUpperCase() })}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder={t('referrals_admin.tracking.code_placeholder')}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">{t('referrals_admin.tracking.max_uses_field')}</label>
                <input
                  type="number"
                  value={trackingForm.max_uses}
                  onChange={(e) => setTrackingForm({ ...trackingForm, max_uses: e.target.value })}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder={t('referrals_admin.tracking.max_uses_placeholder')}
                  min="1"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">{t('referrals_admin.tracking.expires_field')}</label>
                <input
                  type="date"
                  value={trackingForm.expires_at}
                  onChange={(e) => setTrackingForm({ ...trackingForm, expires_at: e.target.value })}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
            </div>
            <div className="mt-4 flex justify-end">
              <button
                disabled={trackingSubmitting || !trackingForm.label.trim()}
                onClick={async () => {
                  setTrackingSubmitting(true);
                  try {
                    await referralAdminService.createTrackingCode({
                      label: trackingForm.label.trim(),
                      code: trackingForm.code.trim() || undefined,
                      max_uses: trackingForm.max_uses ? Number(trackingForm.max_uses) : null,
                      expires_at: trackingForm.expires_at || null,
                    });
                    setTrackingForm({ label: '', code: '', max_uses: '', expires_at: '' });
                    setTrackingFormOpen(false);
                    fetchData();
                  } catch (err) {
                    setError(err instanceof Error ? err.message : t('referrals_admin.tracking.create_error'));
                  } finally {
                    setTrackingSubmitting(false);
                  }
                }}
                className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white rounded-md text-sm font-medium transition-colors"
              >
                {trackingSubmitting ? t('referrals_admin.tracking.creating') : t('referrals_admin.tracking.create_button')}
              </button>
            </div>
          </div>
        )}

        {trackingCodes.length === 0 ? (
          <p className="text-sm text-gray-500">{t('referrals_admin.tracking.empty')}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead>
                <tr className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <th className="px-4 py-3">{t('referrals_admin.tracking.col_label')}</th>
                  <th className="px-4 py-3">{t('referrals_admin.tracking.col_code')}</th>
                  <th className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      {t('referrals_admin.tracking.col_link')}
                      <select
                        value={trackingLang}
                        onChange={(e) => setTrackingLang(e.target.value)}
                        className="text-xs font-normal border border-gray-300 rounded px-1 py-0.5 bg-white text-gray-600"
                      >
                        <option value="en">EN</option>
                        <option value="ru">RU</option>
                        <option value="es">ES</option>
                        <option value="de">DE</option>
                      </select>
                    </div>
                  </th>
                  <th className="px-4 py-3 text-center">{t('referrals_admin.tracking.col_clicks')}</th>
                  <th className="px-4 py-3 text-center">{t('referrals_admin.tracking.col_signups')}</th>
                  <th className="px-4 py-3 text-center">{t('referrals_admin.tracking.col_conversions')}</th>
                  <th className="px-4 py-3">{t('referrals_admin.tracking.col_expires')}</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {trackingCodes.map((code) => (
                  <tr key={code.id}>
                    <td className="px-4 py-3 font-medium text-gray-900">{code.label}</td>
                    <td className="px-4 py-3 font-mono text-indigo-700">{code.code}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span className="text-gray-500 text-xs truncate max-w-[180px]">{code.short_url}</span>
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(code.short_url);
                            setCopiedId(code.id);
                            setTimeout(() => setCopiedId(null), 2000);
                          }}
                          className="text-gray-400 hover:text-indigo-600 flex-shrink-0"
                          title={t('referrals_admin.tracking.copy_link')}
                        >
                          {copiedId === code.id ? (
                            <svg className="w-4 h-4 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                          ) : (
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                            </svg>
                          )}
                        </button>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center text-gray-700">{code.clicks_count}</td>
                    <td className="px-4 py-3 text-center text-gray-700">{code.signups_count}</td>
                    <td className="px-4 py-3 text-center font-semibold text-green-700">{code.conversions_count}</td>
                    <td className="px-4 py-3 text-gray-500 text-xs">
                      {code.expires_at ? new Date(code.expires_at).toLocaleDateString() : '—'}
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={async () => {
                          if (!window.confirm(t('referrals_admin.tracking.delete_confirm'))) return;
                          try {
                            await referralAdminService.deleteTrackingCode(code.id);
                            fetchData();
                          } catch (err) {
                            setError(err instanceof Error ? err.message : t('referrals_admin.tracking.delete_error'));
                          }
                        }}
                        className="text-red-500 hover:text-red-700 text-xs font-medium"
                      >
                        {t('referrals_admin.tracking.delete')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
};
